/**
 * What people wear for the weather: shorts and bare arms on a warm day,
 * long sleeves as it cools, a jacket in the cold or the wet, a hat and
 * gloves in a frost.
 *
 * Everyone dresses for how cold it feels — the air less the wind's chill —
 * but not all alike: each has a hardiness that shifts what they call cold by
 * a few degrees either way, so a crowd changes into jackets over a range of
 * temperatures rather than all at once, and there is always someone in
 * shorts too early in spring.
 *
 * Pure: `three/character/crowd.ts` paints it.
 */

/** The weather as clothing cares for it: `core/weather/weather.ts`'s fields. */
export interface ClothingWeather {
  /** °C. */
  temperature: number;
  /** m/s. */
  wind: number;
  /** mm an hour. */
  rain: number;
  /** cm an hour. */
  snowfall: number;
}

export interface Dress {
  /** What covers the legs: bare (shorts, a skirt), tights under a skirt, or trousers. */
  legs: 'bare' | 'tights' | 'covered';
  sleeves: 'short' | 'long';
  /** A jacket over the top, down past the waist. */
  coat: boolean;
  /** That jacket a bright rain jacket. */
  raincoat: boolean;
  beanie: boolean;
  gloves: boolean;
}

/** How far one person's hardiness moves what they dress for, °C either way. */
export const HARDINESS = 4;

/**
 * How cold it feels, °C: the air, less the wind's chill where it is cool
 * enough for the wind to chill (a simplified wind chill: some 4 °C off at
 * 10 m/s).
 */
export function feelsLike(w: ClothingWeather): number {
  if (w.temperature > 20) return w.temperature;
  return w.temperature - 1.3 * Math.sqrt(Math.max(0, w.wind - 1)) * Math.min(1, (20 - w.temperature) / 10);
}

/**
 * What to wear in `w`, for someone of `hardiness` −1 (feels the cold) to 1
 * (shorts in March), whose usual clothes are a skirt (`skirt`) or shorts
 * (`shorts`). Trousers worn on a hot day turn into shorts on the hardiest.
 */
export function dressFor(w: ClothingWeather, hardiness: number, skirt: boolean, shorts: boolean): Dress {
  const t = feelsLike(w) + HARDINESS * Math.max(-1, Math.min(1, hardiness));
  const raining = w.rain > 0.3, snowing = w.snowfall > 0.05;
  const coat = t < 9 || snowing || (raining && t < 20);
  let legs: Dress['legs'];
  if (skirt) legs = t >= 14 ? 'bare' : t >= -6 ? 'tights' : 'covered';
  else if (shorts) legs = t >= 14 ? 'bare' : 'covered';
  else legs = t >= 26 && hardiness > 0 ? 'bare' : 'covered';
  return {
    legs,
    sleeves: coat || t < 16 ? 'long' : 'short',
    coat,
    raincoat: coat && raining && t >= 2,
    beanie: t < 1 || (snowing && t < 4),
    gloves: t < -3,
  };
}
